import { useState } from 'react'
import { faLandmark } from '@fortawesome/pro-regular-svg-icons'
import { faCheck } from '@fortawesome/pro-solid-svg-icons'
import { UserTaxEstimate } from '@/src/v2-features/taxes/quarterly/userTaxEstimates.slice'
import { checkPastEstimateIsUnpaid } from '@/src/v2-features/taxes/quarterly/userTaxEstimates.selector'
import { Button, Card, CardColors, Icon, Text } from '@/ui'
import { formatCurrencyFromCents } from '@/utils/currencyHelpers'
import { DATE_FORMATS_LUXON, isoToUTCDateTime } from '@/utils/dateHelpers'
import { TextColors, TypographyStyles } from '@/styles'
import { UpdateQuarterlyTaxEstimateDialog } from '../../quarterly/components/UpdateQuarterlyTaxEstimateDialog'

const EditPaymentButton = ({
  setModalOpen,
}: {
  setModalOpen: (_val: boolean) => void
}) => {
  return (
    <Button
      variant="secondary"
      className="w-full"
      onClick={() => setModalOpen(true)}
    >
      Edit Payment
    </Button>
  )
}

export const EstimateCard = ({
  estimate,
  readOnly,
  onPaymentSaved,
}: {
  estimate: UserTaxEstimate
  readOnly?: boolean
  onPaymentSaved?: () => void
}) => {
  const [modalOpen, setModalOpen] = useState(false)
  const unpaid = checkPastEstimateIsUnpaid(estimate)

  const iconBg = estimate.filingState ? 'bg-black' : 'bg-green-500'

  return (
    <Card color={unpaid ? CardColors.LEVEL_1 : CardColors.EMPHASIS}>
      <div className="flex flex-col md:flex-row">
        {/* Left Column */}
        <div className="w-full sm:mb-8 md:mb-0 md:w-1/2">
          <span className={`p-1 inline-block rounded-sm mr-4 ${iconBg}`}>
            <Icon icon={faLandmark} className="text-white" />
          </span>
          <Text variant={TypographyStyles.H3} className="inline-block">
            {estimate.filingState
              ? `State - ${estimate.filingState}`
              : 'Federal'}
          </Text>
        </div>

        {/* Right Column */}
        <div className="w-full md:w-1/2">
          <div className="flex flex-col">
            {unpaid ? (
              <Button
                variant="secondary"
                size="md"
                className="w-full"
                onClick={() => setModalOpen(true)}
              >
                Update Payment
              </Button>
            ) : estimate.quarterPaid ? (
              <>
                <Text
                  variant={TypographyStyles.H1}
                  color={TextColors.GREEN}
                  className="text-left md:text-right mb-2"
                >
                  {formatCurrencyFromCents(estimate.amountPaidInCents)}
                </Text>

                <div className="flex items-center justify-end space-x-1">
                  <Text
                    variant={TypographyStyles.BODY_SM}
                    color={TextColors.MOSS}
                  >
                    paid{' '}
                    {estimate.paidAt &&
                      isoToUTCDateTime(estimate.paidAt).toFormat(
                        DATE_FORMATS_LUXON.DISPLAY_SHORT
                      )}
                  </Text>
                  <Icon
                    icon={faCheck}
                    className="h-4"
                    color={TextColors.GREEN}
                  />
                </div>

                {!readOnly && <EditPaymentButton setModalOpen={setModalOpen} />}
              </>
            ) : (
              <>
                <Text
                  variant={TypographyStyles.BODY_SM}
                  className="text-left md:text-right mb-2"
                >
                  You&apos;ve indicated that you didn&apos;t pay taxes this
                  quarter.
                </Text>

                {!readOnly && <EditPaymentButton setModalOpen={setModalOpen} />}
              </>
            )}
          </div>
        </div>
      </div>

      <UpdateQuarterlyTaxEstimateDialog
        open={modalOpen}
        close={() => setModalOpen(false)}
        estimate={estimate}
        onSaved={onPaymentSaved}
      />
    </Card>
  )
}
